import axios from 'axios'

import { API_BASE_URL } from 'utils'

// VRG-134: Self-serve signup client.
//
// The `/signup/*` endpoints are PUBLIC (unauthenticated) — the visitor is not
// logged in. We deliberately use a BARE axios instance here (not the shared
// `api`/`instance` from services/index) so the auth interceptors don't fire:
// that shared instance bounces to /sign-in on 401 and would swallow the
// intentional 403 the gate returns. A signup visitor has no session to refresh.
const signupClient = axios.create({ baseURL: API_BASE_URL })

export interface PendingSignupCompany {
  id: string
  name: string
  /** The plan whoever invited them expected. Pre-selected, not forced. */
  intendedPriceId?: string | null
}

export interface CheckEligibilityResponse {
  allowed: boolean
  // Present (uuid) when the email's domain matches an existing company → the
  // visitor is an employee joining, not a founder creating a new company.
  matchedCompanyId?: string | null
  // The matched company's display name — show "Join <name>?" before calling
  // joinCompany (which now REQUIRES confirm:true — see joinCompany below).
  matchedCompanyName?: string | null
  // Present when internal staff already created this company and invited them.
  // They pay for it rather than creating one — the create path would refuse
  // them, since they already have a company.
  pendingCompany?: PendingSignupCompany | null
}

export interface JoinCompanyResponse {
  joined: boolean
  companyId: string
}

/**
 * VERA-1095 (double-charge guard): the API's 409 code for "this signup has
 * already paid — do not show a payment form".
 *
 * WHY A CODE AND NOT A MESSAGE. The client has to BRANCH on this: the right
 * response is to send the visitor to /signup/success to VERIFY, which is a
 * navigation, not an error render. Matching on prose would break the first time
 * the copy is edited, and silently — the branch would just stop firing and the
 * visitor would be offered a second payment.
 *
 * Mirrors `SIGNUP_PAYMENT_ALREADY_IN_FLIGHT` in the API's
 * `create-signup-company.use-case.ts`. The two cannot share a module across the
 * app boundary, so each names the other; change one and change both.
 */
export const SIGNUP_PAYMENT_ALREADY_IN_FLIGHT = 'SIGNUP_PAYMENT_ALREADY_IN_FLIGHT'

export interface AlreadyPaidSignup {
  /**
   * The intent to send to /signup/success. NULL is a real possibility, not a
   * type formality — see below — and the caller must handle it rather than
   * assume a redirect target.
   */
  paymentIntentId: string | null
  /** Stripe's status: succeeded | processing | requires_capture. */
  paymentStatus?: string
  /** The API's own sentence, safe to render verbatim. */
  message: string
}

/**
 * Reads the guard's 409 off an axios rejection, or null if this is any other
 * failure.
 *
 * THE WIRE SHAPE IS `{message, code, details}` AND NOTHING ELSE. The API's
 * global `ApplicationExceptionFilter` rewrites every error body to those three
 * fields, so the intent id arrives inside `details` — a sibling of `code` would
 * simply not be there. Read it from anywhere else and this silently never
 * matches.
 *
 * MATCHES ON THE CODE ALONE, and reports a null id rather than declining to
 * match. Requiring the id looks safer and is worse: a 409 whose id went missing
 * would fall through to the generic error path, where the caller's
 * `extractMessage` flattens `details` into a message and would render the bare
 * string "succeeded" to the visitor. Matching on the code keeps every shape of
 * this error inside the branch that knows what it means.
 */
export const readAlreadyPaidError = (err: unknown): AlreadyPaidSignup | null => {
  const response = (
    err as { response?: { status?: number; data?: Record<string, unknown> } } | undefined
  )?.response
  if (response?.status !== 409) return null

  const body = response.data
  if (body?.code !== SIGNUP_PAYMENT_ALREADY_IN_FLIGHT) return null

  const details = (body.details ?? {}) as Record<string, unknown>
  return {
    paymentIntentId:
      typeof details.paymentIntentId === 'string' && details.paymentIntentId
        ? details.paymentIntentId
        : null,
    paymentStatus:
      typeof details.paymentStatus === 'string' ? details.paymentStatus : undefined,
    message:
      typeof body.message === 'string' && body.message.trim()
        ? body.message
        : "We've already received a payment for this signup. Please contact support so we can finish setting up your account.",
  }
}

/**
 * VERA-1095: what the payment step needs to render.
 *
 * Signup pays through Stripe **Elements** now, not a Checkout Session — see
 * SignupPaymentStep for why (an embedded Checkout iframe cannot be styled to
 * match the page). There is no `checkoutUrl` and no `uiMode`/`colorScheme`:
 * appearance is a client-side concern again, so the API has nothing to be told
 * about it.
 */
export interface StartSignupPaymentResponse {
  /** Mount `Elements` with this and confirm it with `PaymentElement`. */
  clientSecret: string
  /** Send back to `verifyPayment` once the browser has confirmed. */
  paymentIntentId: string
  // VERA-1095 (VRG-289): null on the founder path — no company exists until
  // payment succeeds (deferred provisioning). Set on the invited-admin path,
  // whose company already existed.
  companyId: string | null
}

// A self-serve plan the founder can pick from (public GET /signup/plans).
export interface SignupPlan {
  priceId: string
  title: string
  description: string | null
  amount: number
  interval: string
  // Stored as a JSON array or object of feature strings on the plan.
  features: string[] | Record<string, unknown> | null
  // VERA-1095: certificate activations included per billing period before
  // graduated per-activation pricing applies. Matches the authenticated
  // GET /subscriptions/get-all-plans's `included_activations`, camelCased to
  // match the rest of this public payload.
  includedActivations: number
  /**
   * VRG-383: the tier's STABLE identity — `starter` | `individual` |
   * `business` | `scale` — mirrored from `stripe_subscription_products.tier_key`
   * (the authenticated catalog exposes the same column as `tier_key`).
   *
   * Required by the plan step: it renders the shared `PlanComparisonMatrix`,
   * whose every per-tier row is keyed on this. Without it the client falls back
   * to deriving a key from the display TITLE, and a title that no longer strips
   * cleanly makes the whole matrix render as dashes while looking healthy.
   *
   * Nullable, not optional-and-forgotten: a hand-made Stripe product can carry
   * no `vera_catalog` metadata at all, and `resolvePlanTierKey`'s title fallback
   * is what covers that case.
   */
  tierKey: string | null
}

export interface CreateSignupCompanyParams {
  email: string
  // The founder's own name. A self-signup has no WorkOS profile to read it
  // from, so if the form doesn't ask, the account is created nameless.
  firstName: string
  lastName?: string
  // VERA-1095 (Change 2): collected at signup — created directly in WorkOS.
  // Sign-in works with this OR OTP afterwards.
  password: string
  companyName: string
  // VRG-113: chosen `<subdomain>.app.vera.inc`. Omitted means "generate one
  // from the company name" — the API validates format, reserved words and
  // uniqueness during provisioning.
  subdomain?: string
  country?: string
  // VERA-1095 (Change 3): optional onboarding fields.
  employeeCount?: string
  categories?: string[]
  // VERA-1095 (VRG-305 acquisition tiers): the Step 2 "About how many units
  // do you produce a year?" bucket. Optional — persisted to `company_profile`
  // at provisioning when present, but nothing on the signup path depends on
  // it (it only ever soft-preselects a plan card client-side; a manual plan
  // pick always wins — see `pickTierForVolume`'s own doc comment).
  productionVolume?: string
  // Stripe price ID of the selected plan.
  priceId: string
  // The founder ticked "I accept the Terms of Service". The API refuses the request
  // (400, nothing written) unless this is literally `true`, and records the
  // acceptance against `termsVersion` (@repo/legal TERMS_AND_CONDITIONS_VERSION).
  acceptedTerms: true
  termsVersion: string
}

export interface ResolveSignupInviteResponse {
  valid: boolean
  // Set only when valid:true. Absent/null for an unknown or malformed token —
  // never distinguishable from "not yet issued" (see the API's own doc comment
  // on `resolveInvite`), so the UI treats anything short of valid:true as "no
  // invite" and renders the page exactly as it does for a stranger with no
  // `?invite=` param at all.
  email: string | null
  /**
   * VRG-595 — the name this address gave on the marketing-site waitlist, so the
   * form does not ask for it a second time.
   *
   * ⚠️ ABSENT IS NORMAL, in two distinct cases that both mean "we were never
   * told": someone staff allowlisted directly, who never filled in the site's
   * form; and a waitlist row from before the site collected first and last
   * separately, which holds one full name and nothing here (nothing splits it
   * — see the API's column comments for why). Treat absence as "leave the field
   * empty", never as an error state to surface.
   *
   * ⚠️ ONLY THE TOKEN PATH CARRIES THIS. `checkEligibility` takes a typed email
   * from anyone and deliberately returns no name: that would turn an
   * allowed/not-allowed oracle into one that leaks who an address belongs to.
   */
  firstName?: string | null
  lastName?: string | null
}

export interface VerifySignupResponse {
  // false means Stripe hasn't settled the session yet — retry, don't fail.
  activated: boolean
  companyId: string
  status: string
  // VERA-1095 (Change 2): set on a successful activation — store as the
  // accessToken (same mechanism as the dev-session bypass) so the caller
  // lands authenticated instead of hunting for an email.
  token?: string
}

export const signupApi = {
  checkEligibility: async (email: string): Promise<CheckEligibilityResponse> => {
    const response = await signupClient.post('/signup/check-eligibility', { email })
    return response.data
  },

  // VERA-1095: resolves a `/sign-up?invite=<token>` link's token to the
  // invited email, so the page can greet the invitee and prefill their email
  // without it ever appearing in the URL. Always 200 — see the response
  // shape's own doc comment on why a bad token isn't distinguishable from a
  // not-yet-issued one.
  resolveInvite: async (token: string): Promise<ResolveSignupInviteResponse> => {
    const response = await signupClient.post('/signup/resolve-invite', { token })
    return response.data
  },

  listPlans: async (): Promise<SignupPlan[]> => {
    const response = await signupClient.get('/signup/plans')
    return response.data
  },

  /**
   * VERA-1095 (billing address): the country list for the payment step's
   * billing-address form.
   *
   * A PUBLIC mirror of the authenticated `GET /common/countries` that Settings
   * reads — same use case server-side, so the two lists cannot diverge. It has
   * to be this rather than `useCountriesQuery` because that endpoint is
   * `AuthGuard`-gated and a signup visitor has no session yet; firing it here
   * would 401 and leave the picker permanently empty.
   */
  listCountries: async (): Promise<{ code: string; name: string }[]> => {
    const response = await signupClient.get('/signup/countries')
    return response.data
  },

  // VERA-1095 (VRG-289): requires explicit confirmation — this call must only
  // happen after the visitor has seen and agreed to a "Join <company>?"
  // prompt (matchedCompanyName from checkEligibility). The API rejects
  // confirm:false/missing with 400 and writes nothing.
  joinCompany: async (email: string, confirm: true): Promise<JoinCompanyResponse> => {
    const response = await signupClient.post('/signup/join', { email, confirm })
    return response.data
  },

  createCompany: async (
    params: CreateSignupCompanyParams,
  ): Promise<StartSignupPaymentResponse> => {
    const response = await signupClient.post('/signup/company', params)
    return response.data
  },

  // Invited admin whose company already exists: pick a plan and pay for it.
  // Returns the SAME shape as createCompany, because both paths render the
  // identical payment step — a divergence here only ever shows up when an
  // invited admin actually tries to pay, which no test of the founder flow
  // exercises.
  activateCompany: async (
    email: string,
    priceId: string,
  ): Promise<StartSignupPaymentResponse> => {
    const response = await signupClient.post('/signup/checkout', { email, priceId })
    return response.data
  },

  // Called once the browser has confirmed the PaymentIntent. Activation ALSO
  // happens server-side via the `payment_intent.succeeded` webhook, so this is
  // the fast path rather than the only path — someone who closes the tab still
  // ends up activated.
  verifyPayment: async (paymentIntentId: string): Promise<VerifySignupResponse> => {
    const response = await signupClient.post('/signup/verify', {
      payment_intent_id: paymentIntentId,
    })
    return response.data
  },

  // LEGACY: a signup started before the move to Elements, returning via a
  // `?session_id=` link. Kept so a visitor mid-flight across the deploy is not
  // stranded — their pending row has no PaymentIntent to be found by.
  verifyCheckout: async (sessionId: string): Promise<VerifySignupResponse> => {
    const response = await signupClient.post('/signup/verify', { session_id: sessionId })
    return response.data
  },
}
